import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useAuth} from '../../../helpers/AuthContext';
import Alert from '../../Alert';
import Modal from '../../Modal';
import NewsletterComposer from './NewsletterComposer';
import NewsletterCreditsPanel from './NewsletterCreditsPanel';
import {createNewslettersApi} from './newslettersApi';
import {
    describeCancelRefusal,
    describeSendBlock,
    formatDate,
    formatDateTime,
    quotaMonthLabel,
    statusLabel,
    tierLabel,
} from './newsletterState';
import './NewslettersManager.css';

function QuotaSummary({quota, recipientCount}) {
    if (!quota) return null;
    return (
        <p className="nlm-quota">
            <strong>{recipientCount.toLocaleString()}</strong>{' '}
            {recipientCount === 1 ? 'confirmed subscriber' : 'confirmed subscribers'}
            {' · '}
            {tierLabel(quota.tier)}: {quota.used.toLocaleString()} of{' '}
            {quota.cap.toLocaleString()} recipient emails used this month
            {quota.resetsAt ? ` (resets ${formatDate(quota.resetsAt)})` : ''}
            {quota.totalRemaining !== undefined && <span className="nlm-hint">
                {quota.remaining.toLocaleString()} monthly recipient emails left ·{' '}
                {quota.extraAvailable.toLocaleString()} non-expiring extras ·{' '}
                {quota.totalRemaining.toLocaleString()} available in total.
                {!quota.creditsEnabled && ' Extra-credit sending is paused.'}
            </span>}
        </p>
    );
}

function SignupFormSwitch({enabled, busy, storeUrl, onChange}) {
    return (
        <div className="nlm-switch">
            <label className="checkbox" htmlFor="nlm-signup-enabled">
                <input
                    type="checkbox"
                    id="nlm-signup-enabled"
                    name="signupFormEnabled"
                    checked={enabled}
                    disabled={busy}
                    onChange={event => onChange(event.target.checked)}
                />
                <span>Show a newsletter signup form on my store page</span>
            </label>
            <small className="nlm-hint">
                Visitors confirm their address by email before they join. Turning this off stops
                new sign-ups only; your existing subscribers stay subscribed.
                {enabled && storeUrl && (
                    <>
                        {' '}
                        <a href={`${storeUrl}/store`} target="_blank" rel="noreferrer">
                            See it on your store page
                        </a>
                    </>
                )}
            </small>
        </div>
    );
}

function EmptyAudience({enabled, busy, storeUrl, onEnable}) {
    return (
        <div className="nlm-empty">
            <h3>Your audience starts at zero, by design</h3>
            <p>
                Newsletters only go to people who asked for them. Nobody is added for you, not
                even your existing customers: each person signs up on your store page and
                confirms their address from an email we send them.
            </p>
            {enabled ? (
                <p>
                    Your signup form is on.
                    {storeUrl && (
                        <>
                            {' '}Share{' '}
                            <a href={`${storeUrl}/store`} target="_blank" rel="noreferrer">
                                your store page
                            </a>{' '}
                            and your first subscribers will appear here once they confirm.
                        </>
                    )}
                </p>
            ) : (
                <p>
                    <button type="button" className="button" disabled={busy} onClick={onEnable}>
                        Turn on the signup form
                    </button>
                </p>
            )}
        </div>
    );
}

function BlockNotice({block, upgradeUrl, allotments}) {
    return (
        <Alert type="warning" role="status">
            {block.message}
            {allotments && block.kind !== 'empty-audience' && <>{' '}<a href="#newsletter-extra-sends">See extra sends</a></>}
            {block.kind !== 'empty-audience' && upgradeUrl && (
                <>
                    {' '}
                    <a href={upgradeUrl}>See plans</a>
                </>
            )}
        </Alert>
    );
}

function History({sends, onCancel}) {
    if (!sends.length) {
        return <p className="nlm-hint">No newsletters sent yet.</p>;
    }
    return (
        <div className="nlm-table-wrap">
            <table className="nlm-history">
                <thead>
                    <tr>
                        <th>Subject</th>
                        <th>Status</th>
                        <th>Queued</th>
                        <th>Sent</th>
                        <th>Failed</th>
                        <th>Unsubscribed since</th>
                    </tr>
                </thead>
                <tbody>
                    {sends.map(send => (
                        <tr key={send.blastId}>
                            <td>
                                {send.subject}
                                <small className="nlm-hint">
                                    {send.status === 'scheduled' && send.scheduledAt
                                        ? `Scheduled for ${formatDateTime(send.scheduledAt)}`
                                        : formatDateTime(send.createdAt)}
                                </small>
                            </td>
                            <td>
                                {statusLabel(send.status)}
                                {send.cancelable && (
                                    <button
                                        type="button"
                                        className="button secondary nlm-cancel"
                                        onClick={() => onCancel(send)}
                                    >
                                        Cancel send
                                    </button>
                                )}
                            </td>
                            <td>{send.counts.queued.toLocaleString()}</td>
                            <td>{send.counts.sent.toLocaleString()}</td>
                            <td>{send.counts.failed.toLocaleString()}</td>
                            <td>{send.counts.unsubscribedSinceSend.toLocaleString()}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// Remount all drafts, payment callbacks and replay keys when the authenticated shop changes.
export default function NewslettersManager(props) {
    const {channel, account} = useAuth();
    return <NewslettersScreen key={`${channel?.uuid || channel?.id || ''}:${channel?.isRed}:${account?.id || ''}`} {...props} />;
}

function NewslettersScreen({allotments = false}) {
    const {fetch, API_URL, DASHBOARD_URL, channel} = useAuth();
    const api = useMemo(
        () => createNewslettersApi({fetch, apiUrl: API_URL}),
        [fetch, API_URL],
    );
    const [audience, setAudience] = useState(null);
    const [sends, setSends] = useState([]);
    const [loadError, setLoadError] = useState('');
    const [switchBusy, setSwitchBusy] = useState(false);
    const [switchError, setSwitchError] = useState('');
    const [notice, setNotice] = useState(null);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const active = useRef(true);
    const loadVersion = useRef(0);
    useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);

    const load = useCallback(async () => {
        if (!active.current) return;
        const version = ++loadVersion.current;
        try {
            const [audienceResponse, listResponse] = await Promise.all([
                api.audience(),
                api.list(),
            ]);
            if (!active.current || version !== loadVersion.current) return;
            setAudience(audienceResponse.data);
            setSends(listResponse.data || []);
            setLoadError('');
        } catch (err) {
            if (active.current && version === loadVersion.current) {
                setLoadError(err.message || 'Unable to load your newsletters.');
            }
        }
    }, [api]);

    useEffect(() => { load(); }, [load]);

    // Refresh the audience and quota after every send attempt, then show its outcome. The
    // outcome lives here, not in the composer, because a send can leave the screen blocked.
    function handleResult(outcome) {
        if (!active.current) return;
        setNotice(outcome);
        load();
    }

    // A 404 means the send is already gone (cancelled twice, or from another tab), which is the
    // outcome the seller wanted. A 409 means it started first; reload so history shows that.
    async function cancelSend() {
        if (!cancelTarget || cancelling) return;
        const target = cancelTarget;
        setCancelling(true);
        try {
            const response = await api.cancel(target.blastId);
            setNotice({
                type: 'cancelled',
                subject: target.subject,
                count: response.data?.cancelledCount ?? target.counts.queued,
            });
        } catch (err) {
            if (err.status === 404) {
                setNotice({type: 'cancelled', subject: target.subject, count: null});
            } else {
                setNotice({type: 'error', message: describeCancelRefusal(err)});
            }
        } finally {
            setCancelling(false);
            setCancelTarget(null);
            load();
        }
    }

    async function setSignupForm(enabled) {
        setSwitchBusy(true);
        setSwitchError('');
        try {
            const response = await api.setSignupFormEnabled(enabled);
            setAudience(current => ({
                ...current,
                signupFormEnabled: response.data.signupFormEnabled,
            }));
        } catch (err) {
            setSwitchError(err.message || 'Unable to update the signup form.');
        } finally {
            setSwitchBusy(false);
        }
    }

    if (loadError && !audience) {
        return <Alert type="error" role="alert">{loadError}</Alert>;
    }
    if (!audience) {
        return <p className="nlm-hint">Loading newsletters…</p>;
    }

    const {recipientCount, quota, signupFormEnabled} = audience;
    const block = describeSendBlock({recipientCount, quota});
    const upgradeUrl = DASHBOARD_URL ? `${DASHBOARD_URL}/account/plan` : '';
    const storeUrl = channel?.url || '';

    return (
        <div className="nlm-root">
            <h2>Email newsletters</h2>
            <QuotaSummary quota={quota} recipientCount={recipientCount} />

            {loadError && <Alert type="error" role="alert">{loadError}</Alert>}
            {allotments && <NewsletterCreditsPanel api={api} onRefresh={load} />}
            {switchError && <Alert type="error" role="alert">{switchError}</Alert>}
            <SignupFormSwitch
                enabled={signupFormEnabled}
                busy={switchBusy}
                storeUrl={storeUrl}
                onChange={setSignupForm}
            />

            {recipientCount === 0 && (
                <EmptyAudience
                    enabled={signupFormEnabled}
                    busy={switchBusy}
                    storeUrl={storeUrl}
                    onEnable={() => setSignupForm(true)}
                />
            )}
            {notice?.type === 'success' && (
                <Alert type="success" role="status">
                    {notice.result.duplicate
                        ? 'This newsletter was already sent; nothing was sent twice. '
                        : ''}
                    {notice.result.scheduled
                        ? `Scheduled for ${notice.result.recipientCount.toLocaleString()} `
                        : `Queued for ${notice.result.recipientCount.toLocaleString()} `}
                    {notice.result.recipientCount === 1 ? 'subscriber' : 'subscribers'}.
                    {notice.result.scheduled
                        ? ` It goes out ${formatDateTime(notice.result.scheduledAt)}. You can cancel it from the list below until a minute before it starts.`
                        : ' Delivery starts within a few minutes.'}
                </Alert>
            )}
            {notice?.type === 'cancelled' && (
                <Alert type="success" role="status">
                    {notice.subject ? `“${notice.subject}” was cancelled` : 'The newsletter was cancelled'}
                    {notice.count === null
                        ? '. It had already been removed.'
                        : `, and its ${notice.count.toLocaleString()} recipient ${
                            notice.count === 1 ? 'email was' : 'emails were'} returned to ${
                            quota.totalRemaining !== undefined ? 'the original monthly and extra-credit reservation' : 'your allowance'}.`}
                </Alert>
            )}
            {notice?.type === 'error' && (
                <Alert type="error" role="alert">{notice.message}</Alert>
            )}
            {block && block.kind !== 'empty-audience' && (
                <BlockNotice block={block} upgradeUrl={upgradeUrl} allotments={allotments} />
            )}
            {recipientCount > 0 && (quota.totalRemaining !== undefined || block?.kind !== 'plan-required') && (
                <NewsletterComposer
                    api={api}
                    recipientCount={recipientCount}
                    quota={quota}
                    disabled={quota.totalRemaining === undefined && Boolean(block)}
                    onResult={handleResult}
                />
            )}

            <h3>Sent newsletters</h3>
            <History sends={sends} onCancel={setCancelTarget} />

            <Modal
                isOpen={Boolean(cancelTarget)}
                onClose={() => !cancelling && setCancelTarget(null)}
                title="Cancel this scheduled newsletter?"
                size="small"
                footer={(
                    <div className="nlm-actions">
                        <button
                            type="button"
                            className="button secondary"
                            disabled={cancelling}
                            onClick={() => setCancelTarget(null)}
                        >
                            Keep it scheduled
                        </button>
                        <button
                            type="button"
                            className="button"
                            disabled={cancelling}
                            onClick={cancelSend}
                        >
                            {cancelling ? 'Cancelling…' : 'Cancel newsletter'}
                        </button>
                    </div>
                )}
            >
                {cancelTarget && (
                    <>
                        <p><strong>{cancelTarget.subject}</strong></p>
                        <p>
                            It was set to go out {formatDateTime(cancelTarget.scheduledAt)} to{' '}
                            {cancelTarget.counts.queued.toLocaleString()}{' '}
                            {cancelTarget.counts.queued === 1 ? 'subscriber' : 'subscribers'}.
                            Cancelling removes it, and those recipient emails go back to your{' '}
                            {quotaMonthLabel(cancelTarget.scheduledAt)} monthly allowance and any
                            reserved extra credits, according to the original allocation. To send it again,
                            write a new send.
                        </p>
                    </>
                )}
            </Modal>
        </div>
    );
}
